import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Renderer2,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LayoutService } from './service/app.layout.service';
import { AppSidebarComponent } from './app.sidebar.component';
import { AppTopBarComponent } from './app.topbar.component';
import { ThemeConfigComponent } from './theme-config/theme-config.component';

@Component({
  selector: 'app-layout',
  templateUrl: './app.layout.component.html',
  imports: [
    RouterOutlet,
    ConfirmDialogModule,
    ToastModule,
    AppTopBarComponent,
    AppSidebarComponent,
    ThemeConfigComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppLayoutComponent {
  layoutService = inject(LayoutService);
  renderer = inject(Renderer2);
  router = inject(Router);

  menuOutsideClickListener: any;

  profileMenuOutsideClickListener: any;

  appSidebar = viewChild(AppSidebarComponent);

  appTopbar = viewChild(AppTopBarComponent);

  containerClassString = computed(() => {
    const config = this.layoutService.config();

    return [
      'layout-theme-light',
      config.menuMode === 'overlay' ? 'layout-overlay' : '',
      config.menuMode === 'static' ? 'layout-static' : '',
      this.layoutService.staticMenuDesktopInactive() && config.menuMode === 'static'
        ? 'layout-static-inactive'
        : '',
      this.layoutService.overlayMenuActive() ? 'layout-overlay-active' : '',
      this.layoutService.staticMenuMobileActive() ? 'layout-mobile-active' : '',
      config.inputStyle === 'filled' ? 'p-input-filled' : '',
      !config.ripple ? 'p-ripple-disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    effect(() => {
      const count = this.layoutService.overlayOpenCounter();
      if (count === 0) return;

      const sidebar = this.appSidebar();
      const topbar = this.appTopbar();

      if (!this.menuOutsideClickListener && sidebar && topbar) {
        this.menuOutsideClickListener = this.renderer.listen(
          'document',
          'click',
          (event) => {
            const menuBtn = topbar.menuButton()?.nativeElement;
            const isOutsideClicked = !(
              sidebar.el.nativeElement.isSameNode(event.target) ||
              sidebar.el.nativeElement.contains(event.target) ||
              menuBtn?.isSameNode(event.target) ||
              menuBtn?.contains(event.target)
            );

            if (isOutsideClicked) {
              this.hideMenu();
            }
          },
        );
      }

      if (!this.profileMenuOutsideClickListener && topbar) {
        this.profileMenuOutsideClickListener = this.renderer.listen(
          'document',
          'click',
          (event) => {
            const menuEl = topbar.menu()?.nativeElement;
            const menuBtnEl = topbar.topbarMenuButton()?.nativeElement;
            const isOutsideClicked = !(
              menuEl?.isSameNode(event.target) ||
              menuEl?.contains(event.target) ||
              menuBtnEl?.isSameNode(event.target) ||
              menuBtnEl?.contains(event.target)
            );

            if (isOutsideClicked) {
              this.hideProfileMenu();
            }
          },
        );
      }

      if (this.layoutService.staticMenuMobileActive()) {
        this.blockBodyScroll();
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.hideMenu();
        this.hideProfileMenu();
      });

    destroyRef.onDestroy(() => {
      this.unblockBodyScroll();
      if (this.menuOutsideClickListener) {
        this.menuOutsideClickListener();
      }
    });
  }

  hideMenu() {
    this.layoutService.overlayMenuActive.set(false);
    this.layoutService.staticMenuMobileActive.set(false);
    this.layoutService.menuHoverActive.set(false);
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  hideProfileMenu() {
    this.layoutService.profileSidebarVisible.set(false);
    if (this.profileMenuOutsideClickListener) {
      this.profileMenuOutsideClickListener();
      this.profileMenuOutsideClickListener = null;
    }
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi',
        ),
        ' ',
      );
    }
  }
}
